// frontend/src/askos/reader/ContentReaderPage.js
//
// Learner content reader: Coursera-like continuous scroll over one unit's
// Studio-generated/admin-finalized content, from
// GET /tenant/{slug}/askos/reader/{project_id}/{unit_id}.
//
// Faculty-shared TeachingMaterial is NOT shown here — it isn't unit-scoped
// in the student UI, it's its own flat "Faculty Shared Resources" list
// reached from MySubjectsPage, opened via FacultyResourceReaderPage. Kept
// fully separate (not just visually) so Studio content — which keeps
// accumulating run over run, term over term — never buries a faculty
// member's curated picks.
//
// Slide mode was dropped — a real slide format needs its own AI generation
// pass (deck-shaped content, not just re-paginated markdown), which is out
// of scope for now.
//
// No download/edit: read-only markdown render, right-click/copy/drag
// blocked, no export control anywhere on this page (those stay
// faculty-only in TeachingMaterialEditor.js) — same intent as UpSkill's
// SecurePdfViewer approach, adapted for markdown/HTML content instead of
// PDF canvases (nothing here is a raw file the browser could "Save As").
import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import remarkMath from 'remark-math';
import rehypeKatex from 'rehype-katex';
import 'katex/dist/katex.min.css';
import api, { askosAuthHeader } from '../askosApi';
import { normalizeMathForMarkdown } from '../prefsUtils';
import BookmarkButton from '../BookmarkButton';
import './ContentReaderPage.css';

const block = (e) => e.preventDefault();

const ContentReaderPage = ({ slug }) => {
    const { projectId, unitId } = useParams();
    const navigate = useNavigate();
    const [items, setItems] = useState(null);
    const [error, setError] = useState('');

    useEffect(() => {
        api.get(`/tenant/${slug}/askos/reader/${projectId}/${unitId}`, { headers: askosAuthHeader() })
            .then(res => setItems(res.data.studio_content || []))
            .catch(err => setError(err.response?.data?.detail || 'Could not load this unit\'s content.'));
    }, [slug, projectId, unitId]);

    if (error) {
        return (
            <div className="subpage">
                <button type="button" className="secondary-btn" onClick={() => navigate(-1)}>← Back</button>
                <p className="empty-note" style={{ marginTop: 16 }}>{error}</p>
            </div>
        );
    }

    if (!items) {
        return <div className="subpage"><p className="empty-note">Loading…</p></div>;
    }

    return (
        <div className="reader-shell" onContextMenu={block} onCopy={block} onDragStart={block}>
            <header className="reader-header">
                <button type="button" className="secondary-btn" onClick={() => navigate(-1)}>← Back</button>
            </header>

            <div className="reader-content select-none">
                {items.map(item => (
                    <article key={item.id} className="reader-item">
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 10 }}>
                            <h3>{item.title}</h3>
                            <BookmarkButton slug={slug} target={{ content_block_id: item.id }} note={item.title} />
                        </div>
                        <ReactMarkdown remarkPlugins={[remarkMath]} rehypePlugins={[rehypeKatex]}>
                            {normalizeMathForMarkdown(item.body)}
                        </ReactMarkdown>
                    </article>
                ))}
            </div>
        </div>
    );
};

export default ContentReaderPage;
